/* Copy da fila de moderação de packs. Os textos do `ReasonDialog` (título/label/validação por
   ação) ficam em `submission-action-config.js` — fora do escopo desta migração (componente). */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

interface PackSubmissionsListContent {
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly loadError: string;
  readonly loadMoreButton: string;
  readonly reopenedToast: string;
  readonly reviewStartedToast: string;
  readonly actionError: string;
  readonly submissionUpdatedToast: string;
  readonly bulk: {
    readonly selectedLabel: (count: number) => string;
    readonly selectAllAriaLabel: string;
    readonly selectRowAriaLabel: (packId: string, version: string) => string;
    readonly clearLabel: string;
    readonly dialogTitle: (actionLabel: string, count: number) => string;
    readonly dialogDescription: string;
    readonly reasonLabel: string;
    readonly successToast: (count: number) => string;
    readonly partialToast: (succeeded: number, failed: number) => string;
    readonly errorToast: string;
  };
}

const PACK_SUBMISSIONS_LIST_CONTENT: Bilingual<PackSubmissionsListContent> = {
  'pt-BR': {
    pageTitle: 'Moderação de packs',
    pageDescription: 'Fila de submissões de packs — revise, aprove ou peça mudanças.',
    loadError: 'Falha ao carregar a fila de moderação.',
    loadMoreButton: 'Carregar mais',
    reopenedToast: 'Submissão reaberta.',
    reviewStartedToast: 'Revisão iniciada.',
    actionError: 'Falha ao aplicar a ação.',
    submissionUpdatedToast: 'Submissão atualizada.',
    bulk: {
      selectedLabel: (count) =>
        count === 1 ? '1 submissão selecionada' : `${count} submissões selecionadas`,
      selectAllAriaLabel: 'Selecionar submissões elegíveis visíveis',
      selectRowAriaLabel: (packId, version) => `Selecionar ${packId} v${version}`,
      clearLabel: 'Limpar seleção',
      dialogTitle: (actionLabel, count) =>
        count === 1 ? `${actionLabel} em 1 submissão` : `${actionLabel} em ${count} submissões`,
      dialogDescription:
        'A ação e a justificativa serão aplicadas aos alvos congelados. Conflitos aparecem ' +
        'individualmente e não desfazem os itens concluídos.',
      reasonLabel: 'Justificativa do lote',
      successToast: (count) =>
        count === 1 ? 'Ação aplicada em 1 submissão.' : `Ação aplicada em ${count} submissões.`,
      partialToast: (succeeded, failed) =>
        `${succeeded} concluída(s); ${failed} falharam ou mudaram de estado.`,
      errorToast: 'Falha ao executar a ação em lote.',
    },
  },
  'en-US': {
    pageTitle: 'Pack moderation',
    pageDescription: 'Pack submission queue — review, approve, or request changes.',
    loadError: 'Failed to load the moderation queue.',
    loadMoreButton: 'Load more',
    reopenedToast: 'Submission reopened.',
    reviewStartedToast: 'Review started.',
    actionError: 'Failed to apply the action.',
    submissionUpdatedToast: 'Submission updated.',
    bulk: {
      selectedLabel: (count) =>
        count === 1 ? '1 submission selected' : `${count} submissions selected`,
      selectAllAriaLabel: 'Select visible eligible submissions',
      selectRowAriaLabel: (packId, version) => `Select ${packId} v${version}`,
      clearLabel: 'Clear selection',
      dialogTitle: (actionLabel, count) =>
        count === 1 ? `${actionLabel} for 1 submission` : `${actionLabel} for ${count} submissions`,
      dialogDescription:
        'The action and justification will be applied to the frozen targets. Conflicts are ' +
        'reported individually and do not undo completed items.',
      reasonLabel: 'Batch justification',
      successToast: (count) =>
        count === 1 ? 'Action applied to 1 submission.' : `Action applied to ${count} submissions.`,
      partialToast: (succeeded, failed) =>
        `${succeeded} completed; ${failed} failed or changed state.`,
      errorToast: 'Failed to execute the bulk action.',
    },
  },
};

export const usePackSubmissionsListContent = (): PackSubmissionsListContent =>
  useBilingual(PACK_SUBMISSIONS_LIST_CONTENT);
